/**
 * Export Map Fill Utilities
 *
 * Pure functions that apply the rows proposed by the AI-converter's
 * /fill-export-map to a list of export map variables. Matching follows the
 * CSV import: a row matches a variable by exact fullLabel, and a category by
 * exact label within that variable.
 */

import { ExportMapVariable } from "src/types/exportMap.types"
import { ExportMapFillRow } from "src/types/sourceCodes.types"

/**
 * fullLabel -> proposed row, for O(1) lookup while walking variables
 */
export type FillRowIndex = Map<string, ExportMapFillRow>

/**
 * Outcome of filling one variable
 */
export interface FillVariableResult {
  variable: ExportMapVariable
  /** True when the variable code was changed */
  variableChanged: boolean
  /** Number of category codes changed */
  categoryChanges: number
}

/**
 * Result of applying fill rows to a variable list
 */
export interface ApplyFillRowsResult {
  variables: ExportMapVariable[]
  /** Variables whose variable code changed */
  variableCount: number
  /** Categories whose code changed */
  categoryCount: number
  /** fullLabels of rows that matched no variable */
  unmatchedLabels: string[]
}

/**
 * Indexes fill rows by fullLabel. A later row for the same label wins.
 */
export const buildFillRowIndex = (rows: ExportMapFillRow[]): FillRowIndex => {
  const index: FillRowIndex = new Map()
  for (const row of rows) {
    index.set(row.fullLabel, row)
  }
  return index
}

/**
 * Reads a proposed code as the number a max-multi BOOL needs. The service may
 * send it as a string ("3"); anything that is not a whole number is refused
 * rather than written as NaN.
 */
const asMaxMultiCode = (code: number | string | undefined): number | undefined => {
  if (code === undefined) return undefined
  const numeric = typeof code === "number" ? code : Number(code.trim())
  return Number.isFinite(numeric) ? numeric : undefined
}

/**
 * Applies the proposed row (if any) to one variable.
 *
 * - The variable code is set when the row carries a non-empty one; with
 *   `onlyEmpty` it is only set when the current code is empty.
 * - Category codes are set by category label; unknown labels are ignored.
 *   `onlyEmpty` does not gate categories (a generated map always has codes).
 * - A max-multi BOOL keeps the same code twice: in its single category and in
 *   `maxMultiCode`, which is the one `exportMapToCSV` writes. Filling only the
 *   category would therefore be discarded on the next serialization, so the
 *   matched code is written to both.
 *
 * @param variable - The variable to fill
 * @param index - Rows indexed by fullLabel
 * @param onlyEmpty - Only set the variable code when it is currently empty
 * @returns The (possibly unchanged) variable and what changed
 */
export const fillVariable = (
  variable: ExportMapVariable,
  index: FillRowIndex,
  onlyEmpty: boolean
): FillVariableResult => {
  const row = index.get(variable.fullLabel)
  if (!row) {
    return { variable, variableChanged: false, categoryChanges: 0 }
  }

  let variableChanged = false
  let variableCode = variable.variableCode
  const proposed = row.variableCode?.trim() ?? ""
  if (proposed && (!onlyEmpty || variable.variableCode.trim() === "")) {
    if (proposed !== variable.variableCode) {
      variableCode = proposed
      variableChanged = true
    }
  }

  const codeByLabel = new Map<string, number | string>()
  for (const cat of row.categoryCodes) {
    codeByLabel.set(cat.label, cat.code)
  }

  let categoryChanges = 0
  let categories = variable.categories
  if (categories && row.categoryCodes.length > 0) {
    categories = categories.map((cat) => {
      const code = codeByLabel.get(cat.label)
      if (code === undefined || code === cat.code) return cat
      categoryChanges++
      return { ...cat, code }
    })
  }

  // Max-multi BOOL: mirror the matched code into maxMultiCode, which is what
  // exportMapToCSV writes for these rows. The code is looked up by the single
  // category's label, or by the proposed variable code when the service keyed
  // it that way.
  let maxMultiCode: number | undefined
  if (variable.maxMultiCode !== undefined && row.categoryCodes.length > 0) {
    const single =
      variable.categories?.length === 1 ? variable.categories[0] : undefined
    const matched = asMaxMultiCode(
      (single ? codeByLabel.get(single.label) : undefined) ??
        (proposed ? codeByLabel.get(proposed) : undefined)
    )
    if (matched !== undefined && matched !== variable.maxMultiCode) {
      maxMultiCode = matched
      // The category and the max-multi code are one code in two places, so
      // they count as the single change they are.
      if (categoryChanges === 0) categoryChanges++
    }
  }

  if (!variableChanged && categoryChanges === 0) {
    return { variable, variableChanged: false, categoryChanges: 0 }
  }

  const filled: ExportMapVariable = { ...variable, variableCode, categories }
  if (maxMultiCode !== undefined) filled.maxMultiCode = maxMultiCode

  return { variable: filled, variableChanged, categoryChanges }
}

/**
 * Applies fill rows to a variable list.
 *
 * @param variables - Current export map variables
 * @param rows - Rows proposed by /fill-export-map
 * @param onlyEmpty - Only set variable codes that are currently empty
 * @returns New variable list plus change counts and unmatched row labels
 */
export const applyFillRows = (
  variables: ExportMapVariable[],
  rows: ExportMapFillRow[],
  onlyEmpty: boolean
): ApplyFillRowsResult => {
  const index = buildFillRowIndex(rows)
  const knownLabels = new Set(variables.map((v) => v.fullLabel))

  let variableCount = 0
  let categoryCount = 0

  const updated = variables.map((v) => {
    const result = fillVariable(v, index, onlyEmpty)
    if (result.variableChanged) variableCount++
    categoryCount += result.categoryChanges
    return result.variable
  })

  const unmatchedLabels: string[] = []
  for (const label of index.keys()) {
    if (!knownLabels.has(label)) unmatchedLabels.push(label)
  }

  return { variables: updated, variableCount, categoryCount, unmatchedLabels }
}
